import { InventoryHoldArea } from "../../constants"
import type { InventoryLotWithPallet } from "../../types"

import { AdjustmentWarehouseLabel } from "./components"
import { HOLD_AREA_TO_DESTINATION_AREA } from "./constants"
import type {
  AddPalletsAdjustmentFormValues,
  AddPalletsAdjustmentLotFormValues,
  AdjustmentLotFormValues,
  CombinePalletsAdjustmentFormValues,
  LotCorrectionAdjustmentExistingLotFormValues,
  LotCorrectionAdjustmentFormValues,
  LotCorrectionAdjustmentNewLotFormValues,
  QuantityAdjustmentFormValues,
  SplitPalletsAdjustmentFormValues,
} from "./schemas"

import type {
  CreateAddNewPalletsInput,
  CreateAdjustQuantityInput,
  CreateCombinePalletsInput,
  CreateLotBatchCorrectionInput,
  CreateSplitPalletsInput,
  InventoryByProductWarehouse,
  Product,
} from "@/graphql/codegen/graphql"
import type { Option } from "@/types/form-fields"
import { isNil } from "@/utils/utils"
import { formatWarehouseLabel } from "@/utils/warehouse"

export const toWarehouseOptions = (warehouseInventories: InventoryByProductWarehouse[]): Option<string>[] =>
  warehouseInventories.map((inventory) => ({
    value: inventory.warehouseArea!.warehouseAreaId,
    label: formatWarehouseLabel(inventory.warehouseArea!),
    customLabel: <AdjustmentWarehouseLabel warehouseInventory={inventory} />,
  }))

export const toQuantityAdjustmentCreateInput = (
  formValues: QuantityAdjustmentFormValues,
  productId: number,
  quantityByLotId: Record<string, number | null>
): CreateAdjustQuantityInput => ({
  header: {
    warehouseAreaId: formValues.warehouseAreaId,
    productId,
    reason: formValues.adjustmentReason,
  },
  lots: Object.entries(quantityByLotId).map(([rawInventoryId, newUomQty]) => ({
    rawInventoryId,
    newUomQty: newUomQty as number,
  })),
})

export const toCombinePalletsAdjustmentCreateInput = (
  formValues: CombinePalletsAdjustmentFormValues,
  productId: number,
  selectedLots: InventoryLotWithPallet[]
): CreateCombinePalletsInput => ({
  header: { warehouseAreaId: formValues.warehouseAreaId, productId },
  combineTargetReferenceNbr: formValues.targetReferenceNumber!,
  lots: selectedLots
    .filter((lot) => lot.pallet.referenceNbr !== formValues.targetReferenceNumber)
    .map((lot) => ({ rawInventoryId: lot.rawInventoryId })),
})

export const toSplitPalletsAdjustmentCreateInput = (
  formValues: SplitPalletsAdjustmentFormValues,
  productId: number,
  quantityByLotId: Record<string, number | null>
): CreateSplitPalletsInput => ({
  header: { warehouseAreaId: formValues.warehouseAreaId, productId },
  destination:
    formValues.holdArea === InventoryHoldArea.Warehouse
      ? { destinationWarehouseShelvingId: formValues.shelving.warehouseShelvingId }
      : { destinationAreaCode: HOLD_AREA_TO_DESTINATION_AREA[formValues.holdArea] },
  lots: Object.entries(quantityByLotId).map(([rawInventoryId, qtyToMove]) => ({
    rawInventoryId,
    qtyToMove: qtyToMove as number,
  })),
})

export const toAddPalletsAdjustmentCreateInput = (
  formValues: AddPalletsAdjustmentFormValues,
  productId: number
): CreateAddNewPalletsInput => ({
  header: {
    warehouseAreaId: formValues.warehouseAreaId,
    productId,
    reason: formValues.adjustmentReason,
  },
  palletUom: formValues.palletRow.quantityPerPallet ?? undefined,
  pallets: formValues.destinations.map((destination) => ({
    destination:
      destination.holdArea === InventoryHoldArea.Warehouse
        ? { destinationWarehouseShelvingId: destination.shelving.warehouseShelvingId }
        : { destinationAreaCode: HOLD_AREA_TO_DESTINATION_AREA[destination.holdArea] },
    lots: formValues.palletRow.lots.map((lot) => ({
      newUomQty: lot.quantity!,
      packageUomQty: lot.quantityPerPackage!,
      packagingType: lot.packagingType!,
      newLotNbr: lot.lotNumber,
      newBatchNbr: lot.batchNumber,
    })),
  })),
})

const findDuplicateLotIndexes = (
  lots: { lotNumber: string; batchNumber: string | undefined; quantityPerPackage: number | null }[],
  seen: Set<string>,
  duplicateIndexes: Set<number>
): void => {
  lots.forEach(({ lotNumber, batchNumber, quantityPerPackage }, index) => {
    if (!lotNumber || isNil(quantityPerPackage)) return
    const key = `${lotNumber}-${batchNumber}-${quantityPerPackage}`
    if (seen.has(key)) {
      duplicateIndexes.add(index)
    } else {
      seen.add(key)
    }
  })
}

export const filterAddPalletsAdjustmentDuplicateLotIndexes = (
  lots: AddPalletsAdjustmentLotFormValues[]
): Set<number> => {
  const duplicateIndexes = new Set<number>()
  findDuplicateLotIndexes(lots, new Set<string>(), duplicateIndexes)
  return duplicateIndexes
}

export const getAdjustmentLotDefaultFormValues = (product: Product): AdjustmentLotFormValues => ({
  lotNumber: "",
  batchNumber: "",
  quantityPerPackage: product.packageUsWeight ?? null,
  packagingType: product.packagingType ?? null,
  unitCount: null,
  quantity: null,
})

export const toLotCorrectionAdjustmentExistingLotFormValues = (
  lot: InventoryLotWithPallet
): LotCorrectionAdjustmentExistingLotFormValues => ({
  rawInventoryId: lot.rawInventoryId,
  palletReferenceNumber: lot.pallet.referenceNbr,
  originalLotNumber: lot.lotNbr ?? "",
  originalBatchNumber: lot.batchNbr ?? "",
  originalPackageSize: lot.packageSize ?? null,
  originalQuantity: lot.totalUnits ?? null,
  lotNumber: lot.lotNbr ?? "",
  batchNumber: lot.batchNbr ?? "",
  quantity: lot.totalUnits ?? null,
})

export const filterLotCorrectionAdjustmentDuplicateLotIndexes = (
  existingLots: LotCorrectionAdjustmentExistingLotFormValues[],
  newLots: LotCorrectionAdjustmentNewLotFormValues[]
): { existingLots: Set<number>; newLots: Set<number> } => {
  const seen = new Set<string>()
  const existingLotIndexes = new Set<number>()
  const newLotIndexes = new Set<number>()

  findDuplicateLotIndexes(
    existingLots.map((lot) => ({ ...lot, quantityPerPackage: lot.originalPackageSize })),
    seen,
    existingLotIndexes
  )
  findDuplicateLotIndexes(newLots, seen, newLotIndexes)

  return { existingLots: existingLotIndexes, newLots: newLotIndexes }
}

export const filterChangedExistingLots = (
  existingLots: LotCorrectionAdjustmentExistingLotFormValues[]
): LotCorrectionAdjustmentExistingLotFormValues[] =>
  existingLots.filter(
    (lot) =>
      lot.lotNumber !== lot.originalLotNumber ||
      lot.batchNumber !== lot.originalBatchNumber ||
      lot.quantity !== lot.originalQuantity
  )

export const toLotCorrectionAdjustmentCreateInput = (
  formValues: LotCorrectionAdjustmentFormValues,
  productId: number
): CreateLotBatchCorrectionInput => ({
  header: {
    warehouseAreaId: formValues.warehouseAreaId,
    productId,
  },
  existingLots: filterChangedExistingLots(formValues.existingLots).map((lot) => ({
    rawInventoryId: lot.rawInventoryId,
    newLotNbr: lot.lotNumber,
    newBatchNbr: lot.batchNumber,
    newUomQty: lot.quantity,
  })),
  newLots: formValues.newLots.map((lot) => ({
    rawInventoryId: lot.rawInventoryId,
    newLotNbr: lot.lotNumber,
    newBatchNbr: lot.batchNumber,
    newUomQty: lot.quantity!,
    packageUomQty: lot.quantityPerPackage!,
    packagingType: lot.packagingType!,
  })),
})
